"use client"
import { usePathname, useSearchParams } from "next/navigation";
import React, { useEffect, useRef, useState } from "react";

export type SupportsScrollRestoration = {
    isReadyForScrollRestoration?: (isReady: boolean) => void;
};

const STORAGE_KEY = 'scroll-positions:v1';

function readStore(): Record<string, number> {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
}
function writeStore(store: Record<string, number>) {
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(store));
    } catch { }
}
function keyFor(loc: Pick<Location, "pathname" | "search">) {
    return `${loc.pathname}${loc.search}`;
}

// Save most recent Y for the *current* URL
function saveCurrentPosition() {
    const store = readStore();
    store[keyFor(window.location)] = window.scrollY ?? 0;
    writeStore(store);
}

// Only back/forward navigations should restore - not reloads, links or menu clicks.
// The saved Y is read at navigation time, before the still-loading page can overwrite it.
type PendingRestore = { key: string; y: number };
let pendingRestore: PendingRestore | null = null;

function setPendingRestore(key: string) {
    const y = readStore()[key];
    pendingRestore = y !== undefined ? { key, y } : null;
}

if (typeof window !== "undefined") {
    // Full page load from the browser's history (no bfcache)
    const navEntry = performance.getEntriesByType?.("navigation")?.[0] as PerformanceNavigationTiming | undefined;
    if (navEntry?.type === "back_forward") {
        const navUrl = new URL(navEntry.name, window.location.href);
        // only if this module is loaded for the page the browser loaded, not after a later client navigation
        if (keyFor(navUrl) === keyFor(window.location)) {
            setPendingRestore(keyFor(window.location));
        }
    }
    // Client-side back/forward. Fires before Next renders the target page.
    window.addEventListener("popstate", () => setPendingRestore(keyFor(window.location)));
}

export default function withScrollRestoration<P extends SupportsScrollRestoration>(WrappedComponent: React.ComponentType<P>, delay?: number) {
    const EnhancedComponent = (props: P) => {
        const pathname = usePathname();
        const search = useSearchParams();
        const [isChildReady, setIsChildReady] = useState(false);
        // Y to restore once the child is ready. While set, the current position is not saved.
        const restoreTo = useRef<number | null>(null);

        useEffect(() => {
            // set scroll restoration to manual
            if ('scrollRestoration' in history && history.scrollRestoration !== "manual") {
                const oldScrollRestoration = history.scrollRestoration;
                history.scrollRestoration = "manual";

                return () => {
                    history.scrollRestoration = oldScrollRestoration;
                }
            }
        }, []);

        // Pick up a back/forward navigation to this URL. Runs before the save listeners below,
        // and on search-only changes where the component stays mounted.
        useEffect(() => {
            if (!pendingRestore) return;
            const currentKey = keyFor(window.location);
            if (pendingRestore.key === currentKey) {
                restoreTo.current = pendingRestore.y;
            }
            pendingRestore = null;
        }, [pathname, search]);

        // Keep the current page’s position fresh while scrolling (throttled)
        useEffect(() => {
            let ticking = false;
            const onScroll = () => {
                if (!ticking) {
                    ticking = true;
                    requestAnimationFrame(() => {
                        if (restoreTo.current === null) {
                            saveCurrentPosition();
                        }
                        ticking = false;
                    });
                }
            };
            window.addEventListener('scroll', onScroll, { passive: true });
            return () => window.removeEventListener('scroll', onScroll);
        }, []);


        // Save position *before* client-side navigations (capture <a> clicks)
        useEffect(() => {
            const isModified = (e: MouseEvent) =>
                e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0;
            const saveUnlessRestoring = () => {
                if (restoreTo.current === null) {
                    saveCurrentPosition();
                }
            };

            const onClickCapture = (e: MouseEvent) => {
                if (isModified(e)) return;
                let el = e.target as HTMLElement | null;
                while (el && el.tagName !== 'A') el = el.parentElement;
                const a = el as HTMLAnchorElement | null;
                if (!a) return;

                // Only same-origin internal links; skip hashes (browser will handle anchor jumps)
                const url = new URL(a.href, window.location.href);
                const sameOrigin = url.origin === window.location.origin;
                const isHashOnly = url.pathname === window.location.pathname && url.search === window.location.search && url.hash;
                if (sameOrigin && !isHashOnly && !a.target && a.rel !== 'external') {
                    saveUnlessRestoring(); // <- pre-nav save
                }
            };

            document.addEventListener('click', onClickCapture, true);
            // Also save on pagehide (reload, close, cross-origin nav) - a later back navigation can restore it
            window.addEventListener('pagehide', saveUnlessRestoring);
            return () => {
                document.removeEventListener('click', onClickCapture, true);
                window.removeEventListener('pagehide', saveUnlessRestoring);
            };
        }, []);
        // Restore after a back/forward navigation once the child says it's ready
        useEffect(() => {
            if (!isChildReady || restoreTo.current === null) return;
            const y = restoreTo.current;

            const doRestore = () => {
                // Two rAFs give the page a frame to paint layout/content first
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        window.scrollTo({ top: y, behavior: 'auto' });
                        restoreTo.current = null;
                    });
                });
            };

            if (delay && delay > 0) {
                const t = window.setTimeout(doRestore, delay);
                return () => window.clearTimeout(t);
            } else {
                doRestore();
            }
        }, [pathname, search, isChildReady]);

        return <WrappedComponent {...props} isReadyForScrollRestoration={setIsChildReady} />;
    };
    EnhancedComponent.displayName = `WithScrollRestoration(${getDisplayName(WrappedComponent)})`;
    return EnhancedComponent;
}

function getDisplayName<P>(WrappedComponent: React.ComponentType<P>) {
    return WrappedComponent.displayName || WrappedComponent.name || 'Component';
}
